/**
 * CCPRegisterTab — the Critical Control Points register half of the HACCP
 * page. Admins can add a CCP via the form at the top; the list below shows
 * every CCP for the active site with limits + monitoring + corrective
 * actions. Parent owns data fetching.
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";

export default function CCPRegisterTab({ ccps, ccp, setCcp, isAdmin, onSubmit }) {
  return (
    <>
      {isAdmin && (
        <form onSubmit={onSubmit} className="surface p-6 mb-6 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-3 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-eucalyptus-700" />
            <h2 className="font-display font-bold text-lg">Add CCP</h2>
          </div>
          <Input data-testid="ccp-step" placeholder="Process step" value={ccp.step}
            onChange={(e) => setCcp({ ...ccp, step: e.target.value })} className="h-12" required />
          <Input data-testid="ccp-hazard" placeholder="Hazard" value={ccp.hazard}
            onChange={(e) => setCcp({ ...ccp, hazard: e.target.value })} className="h-12" required />
          <Select value={ccp.hazard_type} onValueChange={(v) => setCcp({ ...ccp, hazard_type: v })}>
            <SelectTrigger data-testid="ccp-type" className="h-12"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="biological">Biological</SelectItem>
              <SelectItem value="chemical">Chemical</SelectItem>
              <SelectItem value="physical">Physical</SelectItem>
              <SelectItem value="allergen">Allergen</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="ccp-limit" placeholder="Critical limit" value={ccp.critical_limit}
            onChange={(e) => setCcp({ ...ccp, critical_limit: e.target.value })} className="h-12" required />
          <Input data-testid="ccp-monitor" placeholder="Monitoring" value={ccp.monitoring}
            onChange={(e) => setCcp({ ...ccp, monitoring: e.target.value })} className="h-12" required />
          <Input data-testid="ccp-action" placeholder="Corrective action" value={ccp.corrective_action}
            onChange={(e) => setCcp({ ...ccp, corrective_action: e.target.value })} className="h-12" required />
          <div className="md:col-span-3 flex justify-end">
            <Button type="submit" data-testid="ccp-submit"
              className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white">
              Add CCP
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {ccps.length === 0 && (
          <div className="surface p-10 text-center text-charcoal-soft">No CCPs defined.</div>
        )}
        {ccps.map((c) => (
          <div key={c.id} className="surface p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="label-eyebrow">{c.hazard_type}</div>
                <div className="font-display font-bold text-lg mt-1">{c.step}</div>
                <div className="text-sm text-charcoal-soft mt-1">{c.hazard}</div>
              </div>
              <Badge className="bg-eucalyptus-900 text-white hover:bg-eucalyptus-900 font-mono">
                {c.critical_limit}
              </Badge>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mt-4 text-sm">
              <div>
                <div className="label-eyebrow text-[10px]">Monitoring</div>
                <div className="mt-1">{c.monitoring}</div>
              </div>
              <div>
                <div className="label-eyebrow text-[10px]">Corrective action</div>
                <div className="mt-1">{c.corrective_action}</div>
              </div>
              <div>
                <div className="label-eyebrow text-[10px]">Verification</div>
                <div className="mt-1">{c.verification || "—"}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
